import { PageHeader } from "@shared/components/PageHeader";
import { Button } from "@shared/ui/button";
import {
  ArrowLeft,
  Calendar,
  User,
  Hash,
  Download,
  LogOut,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import ChannelBreakdownList from "../list/list";
import { QuickStatsCard } from "@shared/components/QuickStatsCard";
import { TimelineTab } from "../../../shared/TimelineTab";
import {
  useCurrency,
  useRemoteUtilityId,
} from "@shared/selectors/globalSelectors";
import { useState } from "react";
import { SignOffModal } from "./SignOffModal";
import {
  useReconciliationById,
  useReconciliationSummary,
  useReconciliationExport,
} from "../../../../hooks";

const DayEndReconcilationDetailsLanding = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const currency = useCurrency();
  const remoteUtilityId = useRemoteUtilityId();
  const [showSignOffModal, setShowSignOffModal] = useState(false);

  const { data: summary } = useReconciliationSummary(id || "");
  const { data: reconciliationDetails } = useReconciliationById(
    id,
    remoteUtilityId,
  );

  console.log(reconciliationDetails);

  const { exportCSV, isExporting } = useReconciliationExport(id || "");

  const status = reconciliationDetails?.statusDisplay;
  const periodLabel = `${reconciliationDetails?.periodFrom} - ${reconciliationDetails?.periodTo}`;
  const cashier = reconciliationDetails?.signedOffBy || "N/A";
  const code = reconciliationDetails?.runId;
  const createdDate = reconciliationDetails?.createdDate;

  const getStatusColors = (statusVal: string) => {
    if (!statusVal) return { line: "bg-gray-600", badge: "bg-gray-100 text-gray-800" };
    const lower = statusVal.toLowerCase();
    if (lower === "not started") return { line: "bg-red-600", badge: "bg-red-100 text-red-800" };
    if (lower === "in progress") return { line: "bg-amber-600", badge: "bg-amber-100 text-amber-800" };
    if (lower === "signed off" || lower === "matched") return { line: "bg-green-600", badge: "bg-green-100 text-green-800" };
    return { line: "bg-gray-600", badge: "bg-gray-100 text-gray-800" };
  };

  const statusColors = getStatusColors(status);

  const systemTotal = summary?.kpis?.[0]?.value ?? 0;
  const bankTotal = summary?.kpis?.[1]?.value ?? 0;
  const matchedTotal = summary?.kpis?.[2]?.value ?? 0;
  const varianceVal = summary?.kpis?.[3]?.value ?? 0;

  const kpis = [
    {
      title: "System Posted Total",
      count: `${currency} ${systemTotal}`,
      trendSummary: "Sum of all payments posted by SMART360 for the date",
    },
    {
      title: "Bank Statement Total",
      count: `${currency} ${bankTotal}`,
      trendSummary: "Sum of all credits in the uploaded/fetched bank statement",
    },
    {
      title: "Matched Amount",
      count: `${currency} ${matchedTotal}`,
      trendSummary: "Amount that reconciled without any difference",
    },
    {
      title: "Variance",
      count: (
        <span
          className={Number(varianceVal) < 0 ? "text-red-600" : "text-gray-900"}
        >
          {currency} {varianceVal}
        </span>
      ),
      trendSummary: "System Total minus Bank Total",
    },
  ];

  return (
    <div className="flex flex-col gap-6 p-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <ArrowLeft
            className="w-5 h-5 cursor-pointer text-gray-700"
            onClick={() => navigate("/cx/payments/day-end-reconcilation")}
          />

          {/* Page Header */}
          <div className="flex flex-col">
            <PageHeader
              title="Reconciliation Details"
              subtitle={`View detailed breakdown and variances for reconciliation #${id}`}
            />
          </div>
        </div>

        {/* Export Button */}
        <div className="flex gap-2 items-center">
          <Button variant="outline" onClick={exportCSV} disabled={isExporting}>
            <Download className="w-4 h-4 mr-2" />
            {isExporting ? "Exporting..." : "Export"}
          </Button>
          <Button
            variant="default"
            onClick={() => setShowSignOffModal(true)}
            disabled={status === "Signed Off"}
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign-off with Exceptions
          </Button>
        </div>
      </div>

      {/* File Info Card */}
      <div className="bg-white border rounded-xl overflow-hidden shadow-sm relative">
        <div
          className={`absolute left-0 top-0 bottom-0 w-1.5 ${statusColors.line}`}
        ></div>
        <div className="p-5 pl-7">
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <h1 className="text-lg font-bold text-gray-900">{periodLabel}</h1>
              <span
                className={`px-3 py-1 text-xs font-semibold rounded-full ${statusColors.badge}`}
              >
                {status}
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-500 font-medium">
              <div className="flex items-center gap-1.5">
                <Hash className="h-4 w-4" />
                {code}
              </div>
              <div className="flex items-center gap-1.5">
                <User className="h-4 w-4" />
                {cashier}
              </div>
              <div className="flex items-center gap-1.5">
                <Calendar className="h-4 w-4" />
                Created {createdDate}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-4 gap-4">
        {kpis.map((kpi, index) => (
          <QuickStatsCard
            key={index}
            title={kpi.title}
            count={kpi.count}
            trendSummary={kpi.trendSummary}
          />
        ))}
      </div>

      {/* Channel Breakdown Table */}
      <div className="mt-2">
        <ChannelBreakdownList data={summary?.channelBreakdowns || []} />
      </div>

      {/* Audit Trail */}
      <div className="border border-gray-200 rounded-lg bg-gray-50 shadow-sm overflow-hidden mb-6">
        <div className="p-5 border-b border-gray-100">
          <div className="text-xs font-semibold tracking-wider text-gray-500 uppercase">
            Audit Trail
          </div>
        </div>
        <div className="p-0 [&_.rounded-xl]:rounded-none [&_.border]:border-0 [&_.shadow-sm]:shadow-none bg-white">
          <TimelineTab
            remoteUtilityId={Number(remoteUtilityId) || 1}
            requestId={reconciliationDetails?.runId}
            limit={5}
          />
        </div>
      </div>

      <SignOffModal
        isOpen={showSignOffModal}
        onClose={() => setShowSignOffModal(false)}
        data={reconciliationDetails}
      />
    </div>
  );
};

export default DayEndReconcilationDetailsLanding;
